import type { MouseEvent } from "react"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Activity,
  ArrowUpRight,
  CalendarDays,
  HeartPulse,
  MessageCircle,
  Moon,
  ShieldCheck,
  Sparkles,
  Star,
  Sun,
  Users,
  LogOut,
  ChevronDown,
  Menu,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import GB from "country-flag-icons/react/3x2/GB"
import FR from "country-flag-icons/react/3x2/FR"
import ES from "country-flag-icons/react/3x2/ES"
import DE from "country-flag-icons/react/3x2/DE"
import IT from "country-flag-icons/react/3x2/IT"
import PT from "country-flag-icons/react/3x2/PT"
import NL from "country-flag-icons/react/3x2/NL"
import PL from "country-flag-icons/react/3x2/PL"

import { FadeInSection } from "@/components/FadeInSection"
import { SiteFooter } from "@/components/SiteFooter"
import { useAuth } from "@/contexts/AuthContext"
import { useTheme } from "@/contexts/ThemeContext"
import { supabase } from "@/lib/supabase"
import { getLandingPage } from "@/utils/landingMarkdown"

const stepIcons = [CalendarDays, MessageCircle, Activity] as const

function parseCtaText(input?: string) {
  if (!input) {
    return { label: '', href: undefined as string | undefined }
  }

  const match = input.match(/^(.*?)(?:\s*\(([^)]+)\))?$/)
  return {
    label: match?.[1]?.trim() || input,
    href: match?.[2]?.trim(),
  }
}

interface LandingPageProps {
  initialLanguage?: string
}

function LandingPage({ initialLanguage }: LandingPageProps = {}) {
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [language, setLanguage] = useState<string>(() => {
    // Priority: URL language > localStorage > default 'en'
    if (initialLanguage) return initialLanguage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('health_language_preference')
      if (stored) return stored

      // Detect from URL path
      const path = window.location.pathname
      const pathLang = path.split('/')[1]
      const supportedLangs = ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl']
      if (supportedLangs.includes(pathLang)) return pathLang
    }
    return 'en'
  })
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false)

  // Load landing page content from markdown - EXACTLY like articles
  const [landingPage, setLandingPage] = useState(() => getLandingPage(language))

  // Update landing page when language changes
  useEffect(() => {
    const newLandingPage = getLandingPage(language)
    setLandingPage(newLandingPage)
    if (newLandingPage) {
      document.title = `${newLandingPage.metadata.title} · Diagnoza.care`
    }
  }, [language])

  // Fetch user's language preference from DB (logged in users)
  useEffect(() => {
    const fetchLanguage = async () => {
      if (!user) return

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('language_preference')
          .eq('user_id', user.id)
          .single()

        if (!error && data?.language_preference) {
          const dbLang = data.language_preference
          setLanguage(dbLang)
          // Also save to localStorage as backup
          localStorage.setItem('health_language_preference', dbLang)
        }
      } catch (err) {
        console.error('Error fetching language:', err)
      }
    }

    fetchLanguage()
  }, [user])

  // Handle language change - EXACTLY like articles
  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value
    setIsLoadingLanguage(true)

    // Save to localStorage for anonymous users
    localStorage.setItem('health_language_preference', newLang)

    try {
      if (user) {
        await supabase
          .from('user_profiles')
          .update({ language_preference: newLang })
          .eq('user_id', user.id)
      }

      // Update state and URL
      setLanguage(newLang)
      const newPath = newLang === 'en' ? '/' : `/${newLang}`
      window.history.pushState({}, '', newPath)
    } catch (err) {
      console.error('Error changing language:', err)
    } finally {
      setIsLoadingLanguage(false)
    }
  }

  const handleSmoothScroll = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith("#")) {
      e.preventDefault()
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" })
      }
      setShowMobileMenu(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
  }

  const userName = user?.user_metadata?.name || user?.email?.split("@")[0] || "User"

  // If landing page content not loaded, show loading or fallback
  if (!landingPage) {
    return <div>Loading...</div>
  }

  const t = landingPage.content // translations from markdown

  const navItems = [
    { label: t.nav.howItHelps || 'How it helps', href: '#how' },
    { label: t.nav.steps || 'Steps', href: '#steps' },
    { label: t.nav.stories || 'Stories', href: '#stories' },
    { label: t.nav.healthEncyclopedia || 'Health Encyclopedia', href: '/health' },
    { label: t.nav.pricing || 'Pricing', href: '/pricing' },
    { label: t.nav.contact || 'Contact', href: '#contact' },
  ]

  const parsePlanHighlight = (planName: string, badge?: string) => {
    const normalizedName = planName.toLowerCase()
    const normalizedBadge = badge?.toLowerCase() || ''
    return normalizedName.includes('pro') || normalizedBadge.includes('best') || normalizedBadge.includes('idéal')
  }

  const getPlanCtaHref = (planName: string) => (planName.toLowerCase().includes('pro') ? '/upgrade' : '/signup')

  const primaryContactCta = parseCtaText(t.contact.ctaPrimary)
  const secondaryContactCta = parseCtaText(t.contact.ctaSecondary)
  const contactPrimaryHref = primaryContactCta.href || 'mailto:contact@diagnoza.care'
  const contactSecondaryHref = secondaryContactCta.href || '/pricing'
  const contactPrimaryLabel = primaryContactCta.label || (language === 'fr' ? 'Contacter le support' : 'Contact Support')
  const contactSecondaryLabel = secondaryContactCta.label || (language === 'fr' ? 'Voir les tarifs' : 'View Pricing')

  const userLabel = language === 'fr' ? 'Vous' : 'You'
  const liveChatLabel = language === 'fr' ? 'Chat en direct' : 'Live chat'

  return (
    <div className="min-h-screen bg-slate-50 text-foreground">
      <div className="relative isolate">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-[-10%] h-[480px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.2),_transparent_70%)]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[-12%] top-1/3 h-96 w-96 rounded-full bg-primary/20 blur-[160px]"
        />

        <div className="mx-auto w-full max-w-[1400px] px-4 pb-16 pt-6 sm:px-6 sm:pb-24 sm:pt-8 lg:px-10">
          <header className="sticky top-4 z-40">
            <div className="flex items-center justify-between gap-3 rounded-full border border-white/25 bg-white/10 px-4 py-3 shadow-[0_30px_80px_rgba(15,23,42,0.2)] backdrop-blur-[24px] supports-[backdrop-filter]:bg-white/15 sm:gap-6 sm:px-6">
              <a className="flex items-center gap-2 sm:gap-3" href="#">
                <img
                  src="/web-app-manifest-192x192.webp"
                  alt="Diagnoza"
                  className="h-9 w-9 object-contain sm:h-11 sm:w-11"
                />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground sm:text-[11px] sm:tracking-[0.4em]">Diagnoza</p>
                  <p className="text-base font-semibold leading-none sm:text-lg">care</p>
                </div>
              </a>

              <nav className="hidden items-center gap-6 text-sm font-medium text-muted-foreground lg:flex">
                {navItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => handleSmoothScroll(e, item.href)}
                    className="transition hover:text-foreground"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => void toggleTheme()}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100 dark:hover:bg-slate-800"
                  aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>

                {/* Language Selector - Desktop */}
                <div className="relative hidden sm:block">
                  <div className="absolute left-2 top-1/2 z-10 -translate-y-1/2 pointer-events-none">
                    {language === "en" && <GB className="h-4 w-5" />}
                    {language === "fr" && <FR className="h-4 w-5" />}
                    {language === "es" && <ES className="h-4 w-5" />}
                    {language === "de" && <DE className="h-4 w-5" />}
                    {language === "it" && <IT className="h-4 w-5" />}
                    {language === "pt" && <PT className="h-4 w-5" />}
                    {language === "nl" && <NL className="h-4 w-5" />}
                    {language === "pl" && <PL className="h-4 w-5" />}
                  </div>
                  <select
                    value={language}
                    onChange={handleLanguageChange}
                    disabled={isLoadingLanguage}
                    className="h-9 appearance-none rounded-full border border-slate-200 bg-white/80 pl-9 pr-8 text-sm font-medium text-slate-700 transition hover:bg-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100 dark:hover:bg-slate-800"
                  >
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                    <option value="de">Deutsch</option>
                    <option value="it">Italiano</option>
                    <option value="pt">Português</option>
                    <option value="nl">Nederlands</option>
                    <option value="pl">Polski</option>
                  </select>
                  <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                  </div>
                </div>

                {/* Language Selector - Mobile (Flag only) */}
                <div className="relative sm:hidden">
                  <select
                    value={language}
                    onChange={handleLanguageChange}
                    disabled={isLoadingLanguage}
                    className="h-9 w-9 appearance-none rounded-full border border-slate-200 bg-white/80 transition hover:bg-white focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900/50 dark:hover:bg-slate-800"
                    style={{ color: 'transparent' }}
                    aria-label="Select language"
                  >
                    <option value="en" style={{ color: '#000', backgroundColor: '#fff' }}>English</option>
                    <option value="fr" style={{ color: '#000', backgroundColor: '#fff' }}>Français</option>
                    <option value="es" style={{ color: '#000', backgroundColor: '#fff' }}>Español</option>
                    <option value="de" style={{ color: '#000', backgroundColor: '#fff' }}>Deutsch</option>
                    <option value="it" style={{ color: '#000', backgroundColor: '#fff' }}>Italiano</option>
                    <option value="pt" style={{ color: '#000', backgroundColor: '#fff' }}>Português</option>
                    <option value="nl" style={{ color: '#000', backgroundColor: '#fff' }}>Nederlands</option>
                    <option value="pl" style={{ color: '#000', backgroundColor: '#fff' }}>Polski</option>
                  </select>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                    {language === "en" && <GB className="h-5 w-6" />}
                    {language === "fr" && <FR className="h-5 w-6" />}
                    {language === "es" && <ES className="h-5 w-6" />}
                    {language === "de" && <DE className="h-5 w-6" />}
                    {language === "it" && <IT className="h-5 w-6" />}
                    {language === "pt" && <PT className="h-5 w-6" />}
                    {language === "nl" && <NL className="h-5 w-6" />}
                    {language === "pl" && <PL className="h-5 w-6" />}
                  </div>
                </div>

                {user ? (
                  <>
                    <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
                      <a href="/dashboard">Dashboard</a>
                    </Button>
                    <div className="relative">
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50/50 px-2 py-1.5 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100/50 sm:gap-2 sm:px-3"
                      >
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-400 text-xs font-semibold text-white">
                          {userName.charAt(0).toUpperCase()}
                        </div>
                        <span className="hidden sm:inline">{userName}</span>
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>

                      <AnimatePresence>
                        {showUserMenu && (
                          <>
                            <div
                              className="fixed inset-0 z-[100]"
                              onClick={() => setShowUserMenu(false)}
                            />
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="absolute right-0 top-full z-[101] mt-2 w-48 rounded-2xl border border-white/70 bg-white/95 p-2 shadow-[0_20px_50px_rgba(15,23,42,0.15)] backdrop-blur"
                            >
                              <a
                                href="/dashboard"
                                className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-700 transition hover:bg-emerald-50 sm:hidden"
                              >
                                Dashboard
                              </a>
                              <button
                                onClick={handleSignOut}
                                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-red-600 transition hover:bg-red-50"
                              >
                                <LogOut className="h-4 w-4" />
                                Sign out
                              </button>
                            </motion.div>
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
                      <a href="/signin">Sign in</a>
                    </Button>
                    <Button size="sm" asChild className="hidden sm:inline-flex">
                      <a href="/signup">
                        <Sparkles className="mr-1 size-4" />
                        Start for free
                      </a>
                    </Button>
                  </>
                )}

                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-700 transition hover:bg-slate-50 lg:hidden"
                  aria-label="Toggle menu"
                >
                  {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {showMobileMenu && (
                <>
                  <div
                    className="fixed inset-0 z-[90] bg-black/20 backdrop-blur-sm lg:hidden"
                    onClick={() => setShowMobileMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-4 right-4 top-full z-[95] mt-2 rounded-3xl border border-white/70 bg-white/95 p-4 shadow-[0_20px_50px_rgba(15,23,42,0.15)] backdrop-blur lg:hidden"
                  >
                    <nav className="flex flex-col gap-2">
                      {navItems.map((item) => (
                        <a
                          key={item.label}
                          href={item.href}
                          onClick={(e) => handleSmoothScroll(e, item.href)}
                          className="rounded-xl px-4 py-3 text-base font-medium text-slate-700 transition hover:bg-emerald-50"
                        >
                          {item.label}
                        </a>
                      ))}
                      {!user && (
                        <>
                          <div className="my-2 h-px bg-slate-200" />
                          <a
                            href="/signin"
                            className="rounded-xl px-4 py-3 text-center text-base font-medium text-slate-700 transition hover:bg-slate-50"
                          >
                            Sign in
                          </a>
                          <a
                            href="/signup"
                            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-3 text-center text-base font-medium text-white transition hover:brightness-110"
                          >
                            <Sparkles className="size-4" />
                            Start for free
                          </a>
                        </>
                      )}
                    </nav>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </header>

          <main className="space-y-16 pt-8 sm:space-y-20 sm:pt-12 lg:space-y-36">
            <FadeInSection>
              <section
                id="home"
                className="relative overflow-hidden rounded-3xl border border-white/70 bg-slate-900 text-white shadow-[0_25px_100px_rgba(15,23,42,0.45)] sm:rounded-[48px]"
              >
                <div className="absolute inset-0">
                  <div className="h-full w-full bg-[url('/hero-bg.webp')] bg-cover bg-center opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/75 to-transparent" />
                </div>

                <div className="relative grid gap-8 px-5 py-10 sm:gap-12 sm:px-10 sm:py-16 lg:grid-cols-[1.3fr_0.7fr]">
                  <div className="space-y-6 sm:space-y-8">
                    <Badge className="w-fit bg-white/15 text-white">{t.hero.badge}</Badge>
                    <div className="space-y-4 sm:space-y-6">
                      <h1 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                        {t.hero.headline}
                      </h1>
                      <p className="text-base text-white/80 sm:text-lg lg:text-xl">
                        {t.hero.description}
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                      <Button size="lg" className="w-full px-6 text-sm shadow-lg shadow-emerald-400/30 sm:w-auto sm:px-8 sm:text-base" asChild>
                        <a href="/signup">
                          <HeartPulse className="mr-2 size-4 sm:size-5" />
                          <span className="hidden sm:inline">{t.hero.ctaPrimary}</span>
                          <span className="sm:hidden">{t.hero.ctaPrimary}</span>
                        </a>
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full border-white/50 bg-transparent px-6 text-sm text-white hover:bg-white hover:text-slate-900 sm:w-auto sm:px-8 sm:text-base"
                        asChild
                      >
                        <a href="#steps">
                          <ArrowUpRight className="mr-2 size-4 sm:size-5" />
                          {t.hero.ctaSecondary}
                        </a>
                      </Button>
                    </div>

                    <ul className="grid gap-3 text-sm text-white/85 sm:text-base md:grid-cols-3">
                      {t.hero.bullets.map((bullet) => (
                        <li key={bullet} className="rounded-2xl border border-white/15 bg-white/5 p-3 sm:p-4">
                          {bullet}
                        </li>
                      ))}
                    </ul>

                    <div className="mx-auto max-w-3xl rounded-2xl border border-emerald-200/30 bg-emerald-50/10 p-4 backdrop-blur-sm sm:p-6">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-500/20 sm:h-12 sm:w-12">
                          <ShieldCheck className="h-5 w-5 text-emerald-300 sm:h-6 sm:w-6" />
                        </div>
                        <div className="space-y-1.5 sm:space-y-2">
                          <h2 className="text-base font-semibold text-white sm:text-lg">
                            {t.hero.medicalKnowledgeTitle || 'Built on 200+ Years of Medical Knowledge'}
                          </h2>
                          <p className="text-xs leading-relaxed text-white/75 sm:text-sm">
                            {t.hero.medicalKnowledgeDescription || 'Our AI is trained on two centuries of peer-reviewed medical research, the latest clinical guidelines, and thousands of patient-reported symptom patterns.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 sm:space-y-5">
                    <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur sm:p-5">
                      <p className="text-xs uppercase tracking-[0.4em] text-white/70">{liveChatLabel}</p>
                      <div className="mt-4 space-y-3 text-sm leading-relaxed sm:space-y-4 sm:text-base">
                        <div className="rounded-2xl bg-white/10 p-3 sm:p-4">
                          {userLabel}: &ldquo;{t.hero.chatUserMessage}&rdquo;
                        </div>
                        <div className="rounded-2xl bg-white/15 p-3 sm:p-4">
                          Diagnoza: &ldquo;{t.hero.chatAiResponse}&rdquo;
                          <div className="mt-3 flex flex-wrap gap-2 text-xs text-white/80 sm:gap-3">
                            <span className="flex items-center gap-1">
                              <Activity className="size-3" />
                              {t.hero.chatAnxietyEasing}
                            </span>
                            <span className="flex items-center gap-1">
                              <ShieldCheck className="size-3" />
                              {t.hero.chatHotlineReady}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl bg-white/10 p-3 sm:p-4">
                          {t.hero.chatMemoryUpdate}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-2.5 rounded-3xl border border-white/15 bg-white/5 p-3 text-xs text-white/80 sm:gap-3 sm:p-4 sm:text-sm">
                      {t.hero.stats.map((stat) => (
                        <div
                          key={stat.label}
                          className="flex items-center justify-between rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 sm:px-4 sm:py-3"
                        >
                          <span>{stat.label}</span>
                          <span className="text-base font-semibold text-white sm:text-lg">{stat.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </FadeInSection>

            <FadeInSection>
              <section id="how" className="rounded-3xl border border-slate-200/70 bg-white/95 p-6 backdrop-blur sm:rounded-[40px] sm:p-10">
                <div className="flex flex-wrap items-end justify-between gap-4 sm:gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">{t.nav.howItHelps || 'How it helps'}</p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:mt-3 sm:text-3xl lg:text-4xl">
                      {t.howItHelps.title}
                    </h2>
                  </div>
                  {t.howItHelps.badge && <Badge variant="outline">{t.howItHelps.badge}</Badge>}
                </div>
                <div className="mt-6 grid gap-4 sm:mt-10 sm:gap-6 md:grid-cols-3">
                  {t.howItHelps.cards.map((card) => (
                    <div key={card.title} className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-5 sm:rounded-3xl sm:p-6">
                      <h3 className="text-lg font-semibold text-slate-900 sm:text-xl">{card.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground sm:mt-3">{card.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </FadeInSection>

            <FadeInSection>
              <section id="steps" className="rounded-3xl border border-slate-200/70 bg-gradient-to-r from-white via-emerald-50 to-white p-6 sm:rounded-[40px] sm:p-10">
                <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">{t.steps.subtitle || 'Three gentle steps'}</p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:mt-3 sm:text-3xl lg:text-4xl">
                      {t.steps.title || 'No jargon. Just a steady flow every day.'}
                    </h2>
                  </div>
                  {t.steps.ctaButton && (
                    <Button variant="ghost" className="hidden sm:flex">
                      <ArrowUpRight className="mr-2 size-4" />
                      {t.steps.ctaButton}
                    </Button>
                  )}
                </div>
                <div className="mt-6 grid gap-4 sm:mt-10 sm:gap-6 lg:grid-cols-3">
                  {t.steps.items.map((step, index) => {
                    const Icon = stepIcons[index] || stepIcons[stepIcons.length - 1]
                    return (
                      <div key={step.title} className="rounded-2xl border border-white/70 bg-white/90 p-5 shadow sm:rounded-3xl sm:p-6">
                        <div className="flex items-center gap-2.5 text-sm font-medium text-slate-500 sm:gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-slate-900 sm:h-9 sm:w-9 sm:rounded-2xl">
                            0{index + 1}
                          </span>
                          <Icon className="size-4 text-slate-900 sm:size-5" />
                        </div>
                        <h3 className="mt-3 text-xl font-semibold text-slate-900 sm:mt-4 sm:text-2xl">{step.title}</h3>
                        <p className="mt-2 text-sm text-muted-foreground sm:mt-3">{step.description}</p>
                      </div>
                    )
                  })}
                </div>
              </section>
            </FadeInSection>

            <FadeInSection>
              <section className="grid gap-6 sm:gap-8 lg:grid-cols-[1.1fr_0.9fr]">
                <Card className="border-slate-200/70 bg-white/95">
                  <CardHeader className="space-y-3 p-5 sm:space-y-4 sm:p-6">
                    {t.dailySupport.badge && (
                      <Badge variant="secondary" className="w-fit">
                        {t.dailySupport.badge}
                      </Badge>
                    )}
                    <CardTitle className="text-2xl sm:text-3xl">{t.dailySupport.title}</CardTitle>
                    <CardDescription className="text-sm sm:text-base">
                      {t.dailySupport.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 p-5 pt-0 sm:space-y-4 sm:p-6 sm:pt-0">
                    {t.dailySupport.points.map((item) => (
                      <div key={item.title} className="rounded-2xl border border-slate-200/70 bg-white p-3.5 sm:p-4">
                        <h3 className="text-base font-semibold sm:text-lg">{item.title}</h3>
                        <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-slate-200/70 bg-slate-900 text-white">
                  <CardHeader className="space-y-3 p-5 sm:space-y-4 sm:p-6">
                    <CardTitle className="text-xl sm:text-2xl">{t.trust.title}</CardTitle>
                    <CardDescription className="text-sm text-white/75 sm:text-base">
                      {t.trust.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 p-5 pt-0 text-sm sm:space-y-4 sm:p-6 sm:pt-0">
                    {t.trust.points.map((point) => (
                      <div key={point} className="rounded-2xl bg-white/10 p-3.5 sm:p-4">
                        {point}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </section>
            </FadeInSection>

            <FadeInSection>
              <section id="stories" className="rounded-3xl border border-slate-200/70 bg-white/95 p-6 sm:rounded-[40px] sm:p-10">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">{t.nav.stories || 'Stories'}</p>
                  <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:mt-3 sm:text-3xl lg:text-4xl">
                    {t.stories.title}
                  </h2>
                </div>
                <div className="mt-6 grid gap-4 sm:mt-10 sm:gap-6 md:grid-cols-2">
                  {t.stories.testimonials.map((story) => (
                    <Card key={story.author} className="border-slate-200/70 bg-white">
                      <CardContent className="space-y-4 p-5 sm:space-y-6 sm:p-6">
                        <div className="flex gap-0.5 text-primary sm:gap-1">
                          {Array.from({ length: 5 }).map((_, idx) => (
                            <Star key={idx} className="size-3.5 fill-current sm:size-4" />
                          ))}
                        </div>
                        <p className="text-base text-slate-900 sm:text-lg">{story.quote}</p>
                        <p className="text-sm font-semibold text-muted-foreground">{story.author}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            </FadeInSection>

            <FadeInSection>
              <section id="pricing" className="space-y-4 sm:space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.4em] text-muted-foreground">{t.nav.pricing || 'Pricing'}</p>
                    <h2 className="mt-2 text-2xl font-semibold text-slate-900 sm:mt-3 sm:text-3xl lg:text-4xl">
                      {t.pricing.title}
                    </h2>
                  </div>
                  {t.pricing.badge && (
                    <Badge variant="secondary" className="w-fit">
                      {t.pricing.badge}
                    </Badge>
                  )}
                </div>
                <div className="grid gap-5 sm:gap-6 md:grid-cols-2">
                  {t.pricing.plans.map((plan) => {
                    const highlighted = parsePlanHighlight(plan.name, plan.badge)
                    return (
                      <Card
                        key={plan.name}
                        className={cn(
                          "border-2 bg-white/95",
                          highlighted
                            ? "border-primary shadow-[0_25px_70px_rgba(59,130,246,0.15)]"
                            : "border-slate-200/70"
                        )}
                      >
                        <CardHeader className="p-5 sm:p-6">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground sm:text-sm">{plan.name}</p>
                              <p className="mt-1.5 text-3xl font-semibold text-slate-900 sm:mt-2 sm:text-4xl">{plan.price}</p>
                              {plan.priceCaption && (
                                <p className="mt-0.5 text-sm text-slate-500">{plan.priceCaption}</p>
                              )}
                            </div>
                            {plan.badge && (
                              <Badge className={cn(
                                "shrink-0 text-xs",
                                highlighted ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"
                              )}>
                                {plan.badge}
                              </Badge>
                            )}
                          </div>
                          <CardDescription className="text-sm sm:text-base">{plan.description}</CardDescription>
                          {plan.stats && (
                            <div className="mt-2 sm:mt-3">
                              <Badge variant={highlighted ? "default" : "outline"} className="rounded-full px-3 py-1 text-xs">
                                {plan.stats}
                              </Badge>
                            </div>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-4 p-5 pt-0 sm:p-6 sm:pt-0">
                          <ul className="space-y-2.5 text-sm sm:space-y-3">
                            {plan.features.map((feature) => (
                              <li key={feature} className="flex items-start gap-2">
                                <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                          <Button
                            className={cn(
                              "w-full",
                              highlighted
                                ? "bg-gradient-to-r from-primary to-emerald-500 shadow-lg hover:brightness-110"
                                : ""
                            )}
                            variant={highlighted ? "default" : "outline"}
                            asChild
                          >
                            <a href={getPlanCtaHref(plan.name)}>
                              {highlighted && <HeartPulse className="mr-2 size-4" />}
                              {plan.cta}
                            </a>
                          </Button>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </section>
            </FadeInSection>

            <FadeInSection>
              <section id="contact" className="rounded-3xl border border-slate-200/70 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-6 text-white sm:rounded-[40px] sm:p-10">
                <div className="grid gap-8 sm:gap-10 lg:grid-cols-[1.1fr_0.9fr]">
                  <div className="space-y-4 sm:space-y-6">
                    {t.contact.badge && <Badge className="bg-white/15 text-white">{t.contact.badge}</Badge>}
                    <h2 className="text-2xl font-semibold sm:text-3xl lg:text-4xl">
                      {t.contact.title}
                    </h2>
                    <p className="text-base text-white/80 sm:text-lg">
                      {t.contact.description}
                    </p>
                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-4">
                      <Button className="w-full px-6 text-sm sm:w-auto sm:px-8 sm:text-base" asChild>
                        <a href={contactPrimaryHref}>
                          <Users className="mr-2 size-4 sm:size-5" />
                          {contactPrimaryLabel}
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-white/40 bg-transparent px-6 text-sm text-white hover:bg-white/10 sm:w-auto sm:px-8 sm:text-base"
                        asChild
                      >
                        <a href={contactSecondaryHref}>
                          <ArrowUpRight className="mr-2 size-4 sm:size-5" />
                          {contactSecondaryLabel}
                        </a>
                      </Button>
                    </div>
                  </div>

                  <Card className="border-white/20 bg-white/10 text-white">
                    <CardHeader className="p-5 sm:p-6">
                      <CardTitle className="text-xl sm:text-2xl">{t.contact.cardTitle}</CardTitle>
                      <CardDescription className="text-sm text-white/70 sm:text-base">{t.contact.cardDescription}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 p-5 pt-0 text-sm sm:space-y-4 sm:p-6 sm:pt-0">
                      <div className="rounded-2xl border border-white/20 bg-white/5 p-3.5 sm:p-4">
                        <p className="text-xs text-white/70 sm:text-sm">Care team</p>
                        <p className="mt-1 text-base font-semibold sm:text-lg">{t.contact.careTeam}</p>
                      </div>
                      <div className="rounded-2xl border border-white/20 bg-white/5 p-3.5 sm:p-4">
                        <p className="text-xs text-white/70 sm:text-sm">Partnerships</p>
                        <p className="mt-1 text-base font-semibold sm:text-lg">{t.contact.partnerships}</p>
                      </div>
                      <div className="rounded-2xl border border-white/20 bg-white/5 p-3.5 sm:p-4">
                        <p className="text-xs text-white/70 sm:text-sm">Secure uploads</p>
                        <p className="mt-1 text-base font-semibold sm:text-lg">{t.contact.secureUploads}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </section>
            </FadeInSection>
          </main>

          <SiteFooter className="mt-16 sm:mt-24" />

        </div>
      </div>
    </div>
  )
}

export default LandingPage
